import { DEMO } from "./demoGuard";
import QRCode from "qrcode";
import { buildKot, buildReceipt, code128Svg, encodeEscPos, type KitchenTicket, type Order, type Payment, type PrintDocument, type PrintImage } from "@mango-moons/domain";
import logoUrl from "../assets/logo.png";
import { api, errorMessage, getBackend, toast, useApp } from "../state";
import { desktop, setBusy, type PrintOutcome } from "./desktop";
import type { ApprovalValue } from "./ui";

export type PrinterTarget = {
  /** `system` prints through an installed Windows printer, `network` sends ESC/POS to a LAN printer, `browser` opens the print dialog. */
  mode: "none" | "system" | "network" | "browser";
  deviceName: string; host: string; port: number; widthMm: 58 | 80;
};
export type PrinterConfig = { receipt: PrinterTarget; kitchen: PrinterTarget; autoKot: boolean; autoReceipt: boolean; openDrawer: boolean };
const blank = (): PrinterTarget => ({ mode: "none", deviceName: "", host: "", port: 9100, widthMm: 80 });
const KEY = "mm.printers";

/** Printers belong to the terminal, not the restaurant, so their configuration stays on this device. */
export function loadPrinterConfig(): PrinterConfig {
  const fallback: PrinterConfig = { receipt: blank(), kitchen: blank(), autoKot: true, autoReceipt: true, openDrawer: false };
  try {
    const stored = JSON.parse(localStorage.getItem(KEY) ?? "null") as Partial<PrinterConfig> | null;
    return stored ? { ...fallback, ...stored, receipt: { ...blank(), ...stored.receipt }, kitchen: { ...blank(), ...stored.kitchen } } : fallback;
  } catch { return fallback; }
}
export const savePrinterConfig = (config: PrinterConfig) => localStorage.setItem(KEY, JSON.stringify(config));

const logos = new Map<number, Promise<PrintImage | undefined>>();
/**
 * The logo is gold on black, which would print as a solid black square. For paper it is turned
 * into a negative (dark artwork on white) in greyscale, plus a dithered 1-bit copy for ESC/POS.
 */
export function receiptLogo(widthMm: 58 | 80): Promise<PrintImage | undefined> {
  let ready = logos.get(widthMm);
  if (!ready) logos.set(widthMm, ready = (async () => {
    try {
      const image = new Image();
      image.src = logoUrl;
      await image.decode();
      const size = widthMm === 58 ? 200 : 256;
      const canvas = Object.assign(document.createElement("canvas"), { width: size, height: size });
      const context = canvas.getContext("2d", { willReadFrequently: true })!;
      context.drawImage(image, 0, 0, size, size);
      const pixels = context.getImageData(0, 0, size, size);
      const grey = new Float32Array(size * size);
      for (let index = 0; index < grey.length; index++) {
        const [r, g, b] = [pixels.data[index * 4]!, pixels.data[index * 4 + 1]!, pixels.data[index * 4 + 2]!];
        const value = Math.min(255, Math.max(0, (255 - (0.299 * r + 0.587 * g + 0.114 * b) - 128) * 1.35 + 128));
        grey[index] = value;
        pixels.data.set([value, value, value, 255], index * 4);
      }
      context.putImageData(pixels, 0, 0);
      const src = canvas.toDataURL("image/png");
      // Floyd-Steinberg dithering keeps the shading readable on a thermal head.
      const rowBytes = Math.ceil(size / 8), bits = new Array<number>(rowBytes * size).fill(0);
      for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
        const index = y * size + x, black = grey[index]! < 128, error = grey[index]! - (black ? 0 : 255);
        if (black) bits[y * rowBytes + (x >> 3)]! |= 0x80 >> (x & 7);
        if (x + 1 < size) grey[index + 1]! += error * 7 / 16;
        if (y + 1 < size) { if (x > 0) grey[index + size - 1]! += error * 3 / 16; grey[index + size]! += error * 5 / 16; if (x + 1 < size) grey[index + size + 1]! += error / 16; }
      }
      return { t: "image" as const, src, widthPx: size, heightPx: size, bits };
    } catch { return undefined; }
  })());
  return ready;
}

const escapeHtml = (value: string) => value.replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);

export async function renderHtml(document: PrintDocument): Promise<string> {
  const parts: string[] = [];
  for (const block of document.blocks) {
    if (block.t === "text") parts.push(`<p class="${block.align ?? "left"} ${block.bold ? "b" : ""} ${block.size ?? ""}">${escapeHtml(block.text)}</p>`);
    else if (block.t === "row") parts.push(`<div class="row ${block.bold ? "b" : ""} ${block.size ?? ""}"><span>${escapeHtml(block.left)}</span><span>${escapeHtml(block.right)}</span></div>`);
    else if (block.t === "rule") parts.push("<hr>");
    else if (block.t === "feed") parts.push(`<div style="height:${block.lines * 4}mm"></div>`);
    else if (block.t === "image") parts.push(`<div class="code logo"><img src="${block.src}" alt=""></div>`);
    else if (block.t === "qr") parts.push(`<div class="code qr">${await QRCode.toString(block.data, { type: "svg", margin: 1, errorCorrectionLevel: "M" })}</div>`);
    else parts.push(`<div class="code bar">${code128Svg(block.data, 44, 2)}</div>`);
  }
  const narrow = document.widthMm === 58;
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(document.title)}</title><style>
@page{size:${document.widthMm}mm auto;margin:0}*{box-sizing:border-box}html,body{margin:0;padding:0;background:#fff}
body{width:${document.widthMm}mm;padding:2mm ${narrow ? 2 : 3}mm 6mm;font:${narrow ? 11 : 12}px/1.35 "Segoe UI",Arial,sans-serif;color:#000}
p{margin:0;white-space:pre-wrap;overflow-wrap:anywhere}.center{text-align:center}.right{text-align:right}.b{font-weight:700}
.large{font-size:${narrow ? 14 : 16}px;font-weight:700}.xlarge{font-size:${narrow ? 24 : 30}px;line-height:1.15;font-weight:800}
.row{display:flex;justify-content:space-between;gap:3mm}.row span:first-child{overflow-wrap:anywhere}.row span:last-child{white-space:nowrap;text-align:right;font-variant-numeric:tabular-nums}
hr{border:0;border-top:1px dashed #000;margin:1.5mm 0}.code{text-align:center;margin:2mm 0}.logo img{width:${narrow ? 24 : 30}mm;height:auto}.qr svg{width:${narrow ? 26 : 32}mm;height:auto}.bar svg{max-width:100%;height:11mm}
</style></head><body>${parts.join("")}</body></html>`;
}

export function printInBrowser(html: string): Promise<PrintOutcome> {
  return new Promise((resolve) => {
    const frame = Object.assign(document.createElement("iframe"), { srcdoc: html });
    frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
    frame.onload = () => {
      frame.contentWindow?.focus(); frame.contentWindow?.print();
      setTimeout(() => frame.remove(), 60_000);
      // A browser cannot tell whether the dialog was confirmed or the paper came out.
      resolve({ state: "unknown", message: "Sent to the browser print dialog." });
    };
    document.body.append(frame);
  });
}

export async function sendToPrinter(document: PrintDocument, target: PrinterTarget): Promise<PrintOutcome> {
  // The demo never prints: a demo bill must not come out of the restaurant's kitchen or receipt printer.
  if (DEMO) return { state: "printed", message: "Demo: nothing was printed." };
  if (target.mode === "browser" || (target.mode !== "none" && !desktop)) return printInBrowser(await renderHtml(document));
  if (target.mode === "system") {
    if (!target.deviceName) return { state: "failed", message: "No Windows printer is selected." };
    return desktop!.printer.printHtml({ deviceName: target.deviceName, widthMm: target.widthMm, html: await renderHtml(document) });
  }
  if (target.mode === "network") {
    if (!target.host) return { state: "failed", message: "No printer address is configured." };
    return desktop!.printer.printRaw({ host: target.host, port: target.port, dataBase64: btoa(String.fromCharCode(...encodeEscPos(document))) });
  }
  return { state: "failed", message: "No printer is configured." };
}

const targetName = (target: PrinterTarget) => target.mode === "system" ? target.deviceName : target.mode === "network" ? `${target.host}:${target.port}` : target.mode === "browser" ? "Browser print dialog" : "";

type JobInput = { id?: string; kind: "kot" | "receipt"; orderId: string; ticketId: string | null; reprint: boolean; approval?: ApprovalValue };
/**
 * Persists the job first, prints, then records the real outcome. A job whose outcome is not
 * known stays "unknown" and is never re-sent automatically, so the kitchen cannot receive a
 * silent duplicate ticket.
 */
async function runJob(job: JobInput, document: PrintDocument, target: PrinterTarget): Promise<PrintOutcome | undefined> {
  if (target.mode === "none") return undefined;
  const id = job.id ?? crypto.randomUUID();
  const record = { id, kind: job.kind, orderId: job.orderId, ticketId: job.ticketId, printerName: targetName(target), reprint: job.reprint, approval: job.approval };
  try { await api("print.record", { ...record, state: "queued" }); }
  catch (error) { toast("error", errorMessage(error)); return undefined; }
  setBusy({ printInProgress: true });
  let outcome: PrintOutcome;
  try { outcome = await sendToPrinter(document, target); }
  catch (error) { outcome = { state: "unknown", message: errorMessage(error) }; }
  finally { setBusy({ printInProgress: false }); }
  await api("print.record", { ...record, approval: undefined, state: outcome.state, message: (outcome.message ?? "").slice(0, 300) }).catch(() => undefined);
  if (outcome.state === "failed") toast("error", `${job.kind === "kot" ? "Kitchen ticket" : "Receipt"} did not print: ${outcome.message ?? "printer error"}. Retry from Printers → Print history.`);
  else if (outcome.state === "unknown" && target.mode !== "browser") toast("info", "The printer did not confirm. Check the paper before reprinting.");
  return outcome;
}

export function printKot(ticket: KitchenTicket, options: { reprint?: boolean; approval?: ApprovalValue; jobId?: string } = {}) {
  const config = loadPrinterConfig();
  return runJob({ kind: "kot", orderId: ticket.orderId, ticketId: ticket.id, reprint: options.reprint ?? false, approval: options.approval, ...(options.jobId ? { id: options.jobId } : {}) }, buildKot(ticket, config.kitchen.widthMm, options.reprint), config.kitchen);
}

export async function printReceipt(order: Order, options: { reprint?: boolean; approval?: ApprovalValue; jobId?: string; openDrawer?: boolean } = {}) {
  const config = loadPrinterConfig();
  const state = useApp.getState();
  let payments = state.data.payments.filter((payment) => payment.orderId === order.id);
  if (payments.length === 0 && order.paidMinor > 0) payments = (await getBackend().fetchRange<Payment>("payments", order.businessDate, "9999-12-31")).filter((payment) => payment.orderId === order.id);
  const settings = state.data.settings[0];
  if (!settings) return undefined;
  const document = buildReceipt(order, payments.sort((a, b) => a.createdAt.localeCompare(b.createdAt)), settings, { widthMm: config.receipt.widthMm, logo: await receiptLogo(config.receipt.widthMm), reprint: options.reprint ?? false, openDrawer: (options.openDrawer ?? false) && config.openDrawer });
  return runJob({ kind: "receipt", orderId: order.id, ticketId: null, reprint: options.reprint ?? false, approval: options.approval, ...(options.jobId ? { id: options.jobId } : {}) }, document, config.receipt);
}

/** Waits for a freshly written document to arrive through live sync. */
export async function waitFor<T>(pick: () => T | undefined, timeoutMs = 4000): Promise<T | undefined> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const value = pick();
    if (value !== undefined || Date.now() > deadline) return value;
    await new Promise((resolve) => setTimeout(resolve, 60));
  }
}
export const findTicket = (id: string) => useApp.getState().data.kitchenTickets.find((ticket) => ticket.id === id);
export const findOrder = (id: string) => useApp.getState().data.orders.find((order) => order.id === id);

export async function printTest(target: PrinterTarget, label: string): Promise<PrintOutcome> {
  const logo = await receiptLogo(target.widthMm);
  return sendToPrinter({
    widthMm: target.widthMm, title: "Test print", openDrawer: false,
    blocks: [
      ...(logo ? [logo] : []), { t: "text", text: "Mango Moons Restaurant", align: "center", bold: true, size: "large" }, { t: "text", text: `${label} test - ${target.widthMm} mm`, align: "center" }, { t: "rule" },
      { t: "row", left: "2 x Sample item", right: "1,250.00" }, { t: "row", left: "TOTAL  LKR", right: "1,250.00", bold: true, size: "large" }, { t: "rule" },
      { t: "qr", data: "MM-TEST-PRINT" }, { t: "barcode", data: "MMTEST12345" }, { t: "text", text: "If the rule lines above are one unbroken row, the width is set correctly.", align: "center" }
    ]
  }, target);
}

/**
 * A realistic sample bill and kitchen ticket, rendered exactly as the printer would receive them,
 * so the layout can be checked on screen (Printers → Preview) without using paper.
 */
export async function samplePrintHtml(kind: "receipt" | "kot", widthMm: 58 | 80, settings: Parameters<typeof buildReceipt>[2]): Promise<string> {
  const now = new Date().toISOString();
  if (kind === "kot") {
    return renderHtml(buildKot({
      id: "sample", orderId: "sample", dailyNumber: "D-014", type: "dine_in", tableName: "Table 5", customerPhone: "", batch: 1, incremental: false, note: "Birthday table — serve dessert last",
      lines: [
        { name: "Chicken Kottu", sizeName: "Large", modifiers: ["Hot", "Extra cheese"], quantity: 2, note: "", complimentary: false },
        { name: "Chicken Rice & Curry", sizeName: null, modifiers: [], quantity: 1, note: "No onions", complimentary: false },
        { name: "Lime Soda", sizeName: null, modifiers: [], quantity: 3, note: "", complimentary: false }
      ],
      status: "queued", createdAt: now, createdByName: "Kamal", businessDate: now.slice(0, 10), schemaVersion: 1
    }, widthMm));
  }
  const line = (name: string, unitPriceMinor: number, quantity: number, extra: Partial<Order["lines"][number]> = {}): Order["lines"][number] => ({
    id: name, menuItemId: name, name, sizeName: null, modifiers: [], unitPriceMinor, unitCostMinor: 0, quantity, note: "", complimentary: false, discountMinor: 0, kotBatch: 1, voided: false, categoryId: "", ...extra
  });
  const lines = [line("Chicken Kottu", 165_000, 2, { sizeName: "Large", modifiers: [{ name: "Hot", priceMinor: 0 }, { name: "Extra cheese", priceMinor: 20_000 }] }), line("Chicken Rice & Curry", 95_000, 1, { note: "No onions" }), line("Lime Soda", 35_000, 3), line("Watalappan", 45_000, 1, { complimentary: true })];
  const gross = lines.reduce((sum, entry) => sum + entry.unitPriceMinor * entry.quantity, 0), complimentaryMinor = 45_000, discount = 53_000;
  const subtotal = gross - complimentaryMinor - discount, service = Math.round(subtotal * 0.1), total = subtotal + service;
  const order = {
    id: "sample", ref: "MM261013K7Q4X9PA", publicToken: "0".repeat(40), type: "dine_in", dailyNumber: "D-014", tableName: "Table 5", waiterName: "Kamal", createdByName: "Cashier", customerName: "", customerPhone: "",
    createdAt: now, lines, discounts: [{ kind: "coupon", label: "Coupon MANGO10", amountMinor: discount, scope: "bill" }], serviceChargeBps: 1000, taxBps: 0,
    totals: { grossMinor: gross, complimentaryMinor, lineDiscountMinor: 0, billDiscountMinor: discount, discountMinor: discount, subtotalMinor: subtotal, serviceChargeMinor: service, taxMinor: 0, totalMinor: total, costMinor: 0 },
    paidMinor: total, refundedMinor: 0, status: "completed"
  } as unknown as Order;
  const payments = [{ id: "p1", method: "cash", amountMinor: 300_000, tenderedMinor: 300_000, changeMinor: 0 }, { id: "p2", method: "card", amountMinor: total - 300_000, tenderedMinor: total - 300_000, changeMinor: 0 }] as unknown as Payment[];
  return renderHtml(buildReceipt(order, payments, settings, { widthMm, logo: await receiptLogo(widthMm) }));
}
